import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ScanPutawayLines from '../components/putaway/ScanPutawayLines';
import BatchProcessPutaway from '../components/putaway/BatchProcessPutaway';
import Notification from '../../components/Notification';
import config from '../../utils/config';
import { useAuth } from '../context/wms-context';

const SCANNER_STEPS = {
    collectBatchLines: 'collectBatchLines',
    awaitInventoryLineSelection: 'awaitInventoryLineSelection',
    awaitLocationForSelectedLine: 'awaitLocationForSelectedLine',
    batchReadyToComplete: 'batchReadyToComplete',
};

const normalizeInventoryLineName = (rawInventoryLineName) => {
    const trimmedInventoryLineName = (rawInventoryLineName || '').trim();
    if (!trimmedInventoryLineName) {
        return '';
    }

    const uppercaseInventoryLineName = trimmedInventoryLineName.toUpperCase();
    return uppercaseInventoryLineName.startsWith('INV-') ? uppercaseInventoryLineName : `INV-${uppercaseInventoryLineName}`;
};

const normalizeLocationName = (rawLocationName) => {
    return (rawLocationName || '').trim();
};

export default function WMSPutaway() {
    const ORIGIN = config.origin;
    const { currentUser } = useAuth();
    const authenticatedUserId = currentUser?.authentication?.userId;
    const authenticatedUserEmail = currentUser?.authentication?.userEmail;
    const authenticatedUserCompany = currentUser?.authentication?.userCompany || localStorage.getItem('company');
    const [processBatch, setProcessBatch] = useState(false);
    const [selectedLines, setSelectedLines] = useState([]);
    const [checkingForActiveBatch, setCheckingForActiveBatch] = useState(true);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationData, setNotificationData] = useState({
        title: '',
        desc: '',
        status: '',
    });
    const notificationTimeoutRef = useRef(null);
    const [onScreenError, setOnScreenError] = useState(null);
    const [scannerStep, setScannerStep] = useState(SCANNER_STEPS.collectBatchLines);
    const [activeLineForLocation, setActiveLineForLocation] = useState(null);
    const showScannerDebug = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return new URLSearchParams(window.location.search).get('debug') === '1';
    }, []);

    const handleSetNotification = useCallback((data) => {
        if (data) {
            if (notificationTimeoutRef.current) {
                clearTimeout(notificationTimeoutRef.current);
            }
            setNotificationData({
                title: data.title || '',
                desc: data.desc || '',
                status: data.status || '',
            });
            setShowNotification(true);
            notificationTimeoutRef.current = setTimeout(() => {
                setShowNotification(false);
            }, 6000);
        } else {
            setShowNotification(false);
            clearTimeout(notificationTimeoutRef.current);
        }
    }, []);

    const handleCompleteBatch = async (status) => {
        try {
            const completeBatchResponse = await fetch(`${ORIGIN}/wms/putaway/complete-putaway-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status,
                    authentication: {
                        userId: authenticatedUserId,
                        userEmail: authenticatedUserEmail,
                        userCompany: authenticatedUserCompany,
                    },
                }),
            });
            const completeBatchData = await completeBatchResponse.json();
            if (!completeBatchResponse.ok || completeBatchData?.error) {
                handleSetNotification({
                    title: 'Error Completing Putaway Batch',
                    desc:
                        completeBatchData?.message ||
                        'An error occurred while completing your putaway batch. Please try again or contact your manager.',
                    status: 'error',
                });
                return false;
            }

            setSelectedLines([]);
            setProcessBatch(false);
            setScannerStep(SCANNER_STEPS.collectBatchLines);
            setActiveLineForLocation(null);
            return true;
        } catch (error) {
            handleSetNotification({
                title: 'Error Completing Putaway Batch',
                desc: 'An error occurred while completing your putaway batch. Please try again or contact your manager.',
                status: 'error',
            });
            return false;
        }
    };

    const getEmployeeActivePutawayBatch = useCallback(async () => {
        try {
            const response = await fetch(`${ORIGIN}/wms/putaway/get-employee-active-putaway-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    authentication: {
                        userId: authenticatedUserId,
                        userEmail: authenticatedUserEmail,
                        userCompany: authenticatedUserCompany,
                    },
                }),
            });
            const data = await response.json();

            if (data.error) {
                handleSetNotification({
                    title: 'Error Loading Putaway Batch',
                    desc:
                        data.message ||
                        'An error occurred while loading your putaway batch. Please try again or contact your manager.',
                    status: 'error',
                });
                if (data.message) {
                    setOnScreenError(data.message);
                } else {
                    setOnScreenError(
                        'An error occurred while loading your putaway batch. Please try again or contact your manager.'
                    );
                }
                setCheckingForActiveBatch(false);
                return;
            }

            if (data.length > 0) {
                setSelectedLines(data);
                setProcessBatch(true);
                setScannerStep(SCANNER_STEPS.awaitInventoryLineSelection);
            }
            setCheckingForActiveBatch(false);
        } catch (error) {
            handleSetNotification({
                title: 'Error Loading Putaway Batch',
                desc: 'An error occurred while loading your putaway batch. Please try again or contact your manager.',
                status: 'error',
            });
            setCheckingForActiveBatch(false);
        }
    }, [ORIGIN, authenticatedUserCompany, authenticatedUserEmail, authenticatedUserId, handleSetNotification]);

    const moveInventoryLocation = async (finalLocationName, inventoryId, originalLocationId) => {
        try {
            const locationResponse = await fetch(`${ORIGIN}/wms/putaway/move-inventory-location`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    originalLocationId,
                    finalLocationName,
                    inventoryLineId: inventoryId,
                    authentication: {
                        userId: authenticatedUserId,
                        userEmail: authenticatedUserEmail,
                        userCompany: authenticatedUserCompany,
                    },
                }),
            });

            const response = await locationResponse.json();
            if (response.error) {
                handleSetNotification({
                    title: 'Error Moving Inventory Location',
                    desc: response.message || 'Failed to move inventory location, please try again. If the issue persists',
                    status: 'error',
                });
                return { error: true, message: response.message || 'Location not found or invalid' };
            }
            return response;
        } catch (error) {
            handleSetNotification({
                title: 'Error Moving Inventory Location',
                desc: 'An error occurred while moving inventory location. Please try again or contact your manager.',
                status: 'error',
            });
            return { error: true, message: 'Failed to process location' };
        }
    };

    useEffect(() => {
        getEmployeeActivePutawayBatch();
    }, [getEmployeeActivePutawayBatch]);

    useEffect(() => {
        return () => {
            if (notificationTimeoutRef.current) {
                clearTimeout(notificationTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className='flex flex-col items-center px-2 mt-2 w-full sm:mt-8 sm:px-0'>
            {/* Display a loading spinner if we are checking for an active putaway batch */}
            {checkingForActiveBatch ? (
                <div className='flex flex-col items-center'>
                    <div className='w-28 h-28 rounded-full border-t-2 border-b-2 border-gray-900 animate-spin'></div>
                    <p className='text-gray-500'>Checking for active putaway batch...</p>
                </div>
            ) : (
                <>
                    {/* Display an error message if there is an error in looking up the active putaway batch */}
                    {onScreenError ? (
                        <p className='w-3/4 font-bold text-center text-red-500'>{onScreenError}</p>
                    ) : (
                        <>
                            {/* Display the page if there is no error in looking up the active putaway batch */}
                            {!processBatch ? (
                                <ScanPutawayLines
                                    selectedLines={selectedLines}
                                    setProcessBatch={setProcessBatch}
                                    setSelectedLines={setSelectedLines}
                                    handleSetNotification={handleSetNotification}
                                    setScannerStep={setScannerStep}
                                    setActiveLineForLocation={setActiveLineForLocation}
                                    scannerSteps={SCANNER_STEPS}
                                    normalizeInventoryLineName={normalizeInventoryLineName}
                                    showScannerDebug={showScannerDebug}
                                />
                            ) : (
                                <BatchProcessPutaway
                                    selectedLines={selectedLines}
                                    handleCompleteBatch={handleCompleteBatch}
                                    handleSetNotification={handleSetNotification}
                                    moveInventoryLocation={moveInventoryLocation}
                                    scannerStep={scannerStep}
                                    setScannerStep={setScannerStep}
                                    activeLineForLocation={activeLineForLocation}
                                    setActiveLineForLocation={setActiveLineForLocation}
                                    scannerSteps={SCANNER_STEPS}
                                    normalizeInventoryLineName={normalizeInventoryLineName}
                                    normalizeLocationName={normalizeLocationName}
                                    showScannerDebug={showScannerDebug}
                                />
                            )}
                        </>
                    )}
                    <Notification
                        showSavedNotification={showNotification}
                        setShowSavedNotification={handleSetNotification}
                        title={notificationData.title}
                        desc={notificationData.desc}
                        status={notificationData.status}
                    />
                </>
            )}
        </div>
    );
}
